import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { Dropdown, Modal } from 'react-bootstrap'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import DeleteModal from 'pages/parts/DeleteModal'
import ModalSearchClient from 'pages/parts/ModalSearchClient'

import { httpOk, GET, POST, PUT, DELETE } from 'helpers/api'
import { returnSign, formatDate, regexPhoneNumber } from "helpers/helpers"

// Статусы карты: 0 — свободна (в пачке), 1 — выдана клиенту, -1 — заблокирована
const STATUS_FREE = 0
const STATUS_ISSUED = 1
const STATUS_BLOCKED = -1

function returnStatusName(status) {
	if (Number(status) === STATUS_FREE) return 'cards_status_free'
	if (Number(status) === STATUS_BLOCKED) return 'cards_status_blocked'
	return 'cards_status_issued'
}

function returnClassStatus(status) {
	if (Number(status) === STATUS_FREE) return 'bg-secondary text-white'
	if (Number(status) === STATUS_BLOCKED) return 'bg-danger text-white'
	return 'bg-success text-white'
}

function Cards() {
	const { t } = useTranslation()
	const location = useLocation()

	const [data, setData] = useState([])
	const [selectedItem, setSelectedItem] = useState({})
	const [showModal, setShowModal] = useState(false)
	const [modalSearchClient, setModalSearchClient] = useState(false)
	const [modalDelete, setModalDelete] = useState(false)
	const [deleteModalParams, setDeleteModalParams] = useState({ id: 0, index: 0 })
	const [modalStatus, setModalStatus] = useState(false)
	const [statusParams, setStatusParams] = useState({ id: 0, action: '' })
	const [filterDataExist, setFilterDataExist] = useState(false)
	// Со страницы «Выпуск карт» приходят с ?batch_id= — сразу показываем карты этой партии
	const [filterData, setFilterData] = useState({
		search: '',
		status: '',
		batch_id: new URLSearchParams(location.search).get('batch_id') ?? '',
	})
	const [pagination, setPagination] = useState({
		url: '/clients-cards',
		response: null
	})

	async function searchWithFilter() {
		var filterDataCopy = { ...filterData }
		var filterUrl = "/clients-cards"
		var urlParams = ""
		checkFilter()

		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
		if (filterDataCopy.status !== '')
			urlParams += returnSign(urlParams) + 'status=' + filterDataCopy.status
		if (filterDataCopy.batch_id)
			urlParams += returnSign(urlParams) + 'batch_id=' + filterDataCopy.batch_id
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		if (`/clients-cards${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams
		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}
	}

	function checkFilter() {
		if (!filterData.search && filterData.status === '' && !filterData.batch_id) {
			setFilterDataExist(false)
		}
	}

	function resetFilter() {
		setFilterData({
			search: '',
			status: '',
			batch_id: '',
		})
		setFilterDataExist(false)
	}

	// Привязка новой карты и перевыпуск существующей — одна форма:
	// у новой нет id, у существующей есть.
	async function createUpdate(e) {
		e.preventDefault()

		var response
		if (selectedItem.id) {
			response = await PUT('/clients-cards', selectedItem, { loader: true })
		} else {
			response = await POST('/clients-cards', selectedItem, { loader: true })
		}

		if (httpOk(response)) {
			setShowModal(false)
			searchWithFilter()
		}
	}

	function toggleModalClients(bool = false) {
		setModalSearchClient(bool)
	}

	function handleSelectClient(item = {}) {
		setSelectedItem({
			...selectedItem,
			'client_id': item.id,
			'client_name': item.first_name,
			'client_phone': item.phone1,
		})
		toggleModalClients()
	}

	async function changeStatus() {
		const response = await PUT('/clients-cards-status', statusParams, { loader: true })
		if (httpOk(response)) {
			setModalStatus(false)
			searchWithFilter()
		}
	}

	function toggleModalStatus(item, action) {
		setStatusParams({ id: item.id, action: action })
		setModalStatus(true)
	}

	async function deleteItem() {
		const response = await DELETE(`/clients-cards/${deleteModalParams.id}`, {}, { loader: true })
		if (httpOk(response)) {
			setModalDelete(false)
			searchWithFilter()
		}
	}

	function toggleModal(bool = false, item = {}) {
		setSelectedItem(item)
		setShowModal(bool)
	}

	async function paginate(page) {
		setFilterData({ ...filterData, 'page': page })
	}

	useEffect(() => {
		searchWithFilter()
	}, [filterData.search, filterData.status, filterData.batch_id, filterData.page]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('clients_cards')} [{data.total}]
				</h4>
				<div className="d-flex">
					<button className="btn-rounded btn btn-primary"
						onClick={() => toggleModal(true)}>
						{t('link_card')}
					</button>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex gap-2 mb-3">
						<div>
							<FilterSearch
								filterData={filterData}
								setFilterData={setFilterData}>
							</FilterSearch>
						</div>
						<div>
							<select className="form-select"
								value={filterData.status}
								onChange={(e) => setFilterData({ ...filterData, 'status': e.target.value, 'page': 0 })}>
								<option value="">{t('status')}: {t('all')}</option>
								<option value={STATUS_ISSUED}>{t('cards_status_issued')}</option>
								<option value={STATUS_FREE}>{t('cards_status_free')}</option>
								<option value={STATUS_BLOCKED}>{t('cards_status_blocked')}</option>
							</select>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
								{filterData.status !== '' &&
									<span className="me-2">
										<b>{t('status')}: </b>
										<span>{t(returnStatusName(filterData.status))}</span>
									</span>
								}
								{filterData.batch_id &&
									<span className="me-2">
										<b>{t('cards_batch')}: </b>
										<span>{filterData.batch_id}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase"
									onClick={resetFilter}>
									{t('reset')}
								</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '18%' }}>{t('card_number')}</th>
									<th style={{ 'width': '12%' }} className="text-center">{t('status')}</th>
									<th style={{ 'width': '22%' }}>{t('client')}</th>
									<th style={{ 'width': '16%' }}>{t('phone')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('cards_batch')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('date')}</th>
									<th style={{ 'width': '12%' }}>{t('created_by')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{item.row_number}</td>
										<td>{item.card_number}</td>
										<td className={`text-center text-nowrap ${returnClassStatus(item.status)}`}>
											{t(returnStatusName(item.status))}
										</td>
										<td className="td-ellipsis">{item.client_name}</td>
										<td>{regexPhoneNumber(item.client_phone)}</td>
										<td className="text-center">{item.batch_id}</td>
										<td className="text-center">{formatDate(item.issued_at)}</td>
										<td>{item.created_by}</td>
										<td>
											<div className="table-dropdown">
												<Dropdown id={`dropdown-${index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>

													<Dropdown.Menu>
														{Number(item.status) === STATUS_FREE &&
															<div className="table-dropdown-item"
																onClick={() => toggleModal(true, item)}>
																<i className="uil-user-plus"></i>
																{t('cards_issue')}
															</div>
														}
														{Number(item.status) === STATUS_ISSUED &&
															<div className="table-dropdown-item"
																onClick={() => toggleModal(true, item)}>
																<i className="uil-edit-alt"></i>
																{t('edit')}
															</div>
														}
														{Number(item.status) === STATUS_ISSUED && item.batch_id &&
															<div className="table-dropdown-item"
																onClick={() => toggleModalStatus(item, 'unbind')}>
																<i className="uil-user-times"></i>
																{t('cards_unbind')}
															</div>
														}
														{Number(item.status) !== STATUS_BLOCKED &&
															<div className="table-dropdown-item text-danger"
																onClick={() => toggleModalStatus(item, 'block')}>
																<i className="uil-lock"></i>
																{t('cards_block')}
															</div>
														}
														{Number(item.status) === STATUS_BLOCKED &&
															<div className="table-dropdown-item"
																onClick={() => toggleModalStatus(item, 'unblock')}>
																<i className="uil-unlock"></i>
																{t('cards_unblock')}
															</div>
														}
														{!item.batch_id &&
															<div className="table-dropdown-item text-danger"
																onClick={() => {
																	setDeleteModalParams({ id: item.id, index: index });
																	setModalDelete(true)
																}}>
																<i className="uil-times"></i>
																{t('delete')}
															</div>
														}
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={paginate}>
						</FilterPagination>
					}
				</div>
			</div>

			{/* MODAL CLIENT CARD */}
			{/* На время выбора клиента форму прячем, чтобы модалки не наслаивались:
				selectedItem остаётся, поэтому после выбора форма возвращается заполненной */}
			<Modal show={showModal && !modalSearchClient} animation={false} centered
				size="md"
				onHide={() => toggleModal()}>
				<Modal.Header closeButton>
					<Modal.Title>
						{!selectedItem.id ? t('link_card') :
							Number(selectedItem.status) === STATUS_FREE ? t('cards_issue') : t('edit')
						}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={(e) => createUpdate(e)} autoComplete="off">
						<div className="form-group w-100">
							<label>{t('client')}<span className="required-mark">*</span></label>
							<div className="d-flex gap-2">
								<input type="text" className="form-control"
									readOnly
									placeholder={t('select_client')}
									value={selectedItem.client_name ?
										`${selectedItem.client_name} ${regexPhoneNumber(selectedItem.client_phone)}` : ''}
									onClick={() => toggleModalClients(true)}
									onChange={() => { }} />
								<button type="button" className="btn btn-outline-primary text-nowrap"
									onClick={() => toggleModalClients(true)}>
									{t('select_client')}
								</button>
							</div>
						</div>

						<div className="form-group w-100">
							<label>{t('card_number')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control" name="card_number" autoFocus
								value={selectedItem.card_number ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'card_number': e.target.value.replace(/[^0-9]/g, '') })} />
						</div>

						<div className="d-flex w-100 mt-3 gap-2">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModal()}>{t('cancel')}</button>
							<button type="submit" className="btn btn-primary w-100"
								disabled={!selectedItem.client_id || String(selectedItem.card_number ?? '').length < 4}>
								{t('save')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL CLIENT CARD */}

			{modalSearchClient &&
				<ModalSearchClient
					modalSearchClient={modalSearchClient}
					handleSelectClient={handleSelectClient}
					toggleModalClients={toggleModalClients}>
				</ModalSearchClient>
			}

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>

			<DeleteModal
				modalDelete={modalStatus}
				setModalDelete={setModalStatus}
				deleteItem={changeStatus}>
			</DeleteModal>
		</>
	)
}

export default Cards
